import { Dialog, DialogProps } from "../ui/Dialog"
import { Button } from "../ui/Button"
import { useTranslation } from "react-i18next"
import { pushDialog, popDialog } from "@/store/dialogs-state"
import { useState } from "react"
import clsx from "clsx"
import { UploadPresetDialog } from "./UploadPresetDialog"
import { PresetItem } from "@/store/presets/presets-service"
import imagesUrl from "@assets/edit_small.png?url"
import advancedUrl from "@assets/advanced_mode.png?url"
import { confirmCancelStyleCreation } from "@/store/style/create-style"

export type SetupChoice = "color_match" | "preset"

const uploadPresetDialog = () => {
	return new Promise<PresetItem | undefined>((resolve) => {
		pushDialog(UploadPresetDialog, {
			trackingViewName: "upload_preset_dialog",
			onSelect: (preset) => {
				resolve(preset)
				popDialog()
			},
			onContinue: () => {
				resolve(undefined)
				popDialog()
			},
			canGoBack: true,
			disableClose: false,
			closeConfirm: confirmCancelStyleCreation,
		})
	})
}

export const MissingEditMetadataDialog = (
	props: DialogProps & {
		onSelect?: (preset: PresetItem) => void
		skip?: () => void
		title?: string
		description?: string
	},
) => {
	const { t } = useTranslation("components")
	const [selected, setSelected] = useState<SetupChoice>("color_match")

	const handleContinue = async () => {
		if (selected === "color_match") {
			props.skip?.()
		} else if (selected === "preset") {
			const preset = await uploadPresetDialog()
			if (preset) {
				props.onSelect?.(preset)
			} else {
				props.skip?.()
			}
		}
	}

	const bottomButtons = () => {
		return (
			<div className="flex w-full flex-row items-center justify-end">
				<Button
					trackingLabel="missing_metadata_continue"
					aria-label={t("missingEditMetadataDialog.continueWithoutMetadata")}
					variant="primary"
					onClick={() => void handleContinue()}
					disabled={!selected}
				>
					{t("common:action.continue")}
				</Button>
			</div>
		)
	}

	return (
		<Dialog
			contentSize="md-h-auto"
			{...props}
			trackingViewName="missing_edit_metadata_dialog"
			bottomButtons={bottomButtons()}
		>
			<div className="flex size-full flex-1 flex-col">
				<div className="text-md font-bold">
					{props.title ?? t("missingEditMetadataDialog.defaultTitle")}
				</div>
				<div className="pt-2 text-sm text-base-400">
					{props.description ?? t("missingEditMetadataDialog.defaultDescription")}
				</div>

				<div className="flex h-full flex-row items-start space-x-4 pt-5">
					<div
						role="option"
						aria-selected={selected === "color_match"}
						aria-label={t("missingEditMetadataDialog.useAiColorMatch")}
						className={clsx(
							"outline outline-2 outline-offset-[-2px]",
							selected === "color_match"
								? "outline-accent-600"
								: "outline-transparent",
							"flex size-full cursor-pointer flex-col items-center justify-center space-y-4 rounded-lg bg-base-700 p-8 transition-all hover:bg-base-700/80",
						)}
						onClick={() => setSelected("color_match")}
					>
						<img className="w-1/3 self-center object-cover" src={advancedUrl} />
						<div className="text-center text-sm font-bold">
							{t("missingEditMetadataDialog.useAiColorMatchButton")}
						</div>
					</div>

					<div
						role="option"
						aria-selected={selected === "color_match"}
						aria-label={t("missingEditMetadataDialog.usePreset")}
						className={clsx(
							"outline outline-2 outline-offset-[-2px]",
							selected === "preset" ? "outline-accent-600" : "outline-transparent",
							"flex size-full cursor-pointer flex-col items-center justify-center space-y-4 rounded-lg bg-base-700 p-8 transition-all hover:bg-base-700/80",
						)}
						onClick={() => setSelected("preset")}
					>
						<img className="w-1/3 self-center object-cover" src={imagesUrl} />
						<div className="text-center text-sm font-bold">
							{t("missingEditMetadataDialog.uploadPreset")}
						</div>
					</div>
				</div>
			</div>
		</Dialog>
	)
}
